import cloneDeep from 'lodash/cloneDeep';
import { getUserDataPath, prepareLocalPathInfo } from '@mimir/web/desktop/utils';
import EventBus, { showToast, EVENTS, TOAST } from '@mimir/web/eventBus';
import Timeline from '@mimir/client-common/timeline/Timeline';
import type { TimeRange } from '@mimir/common/util/timeRange';
import type { SubtitleDownloadConfig, TranscriptChoice } from '@mimir/web/pages/item/components/constants';
import { SrtType } from '@mimir/web/pages/item/components/constants';
import { writeAndImportSrt } from '@mimir/web/premiere/writeAndImportSrt';
import { getFolderAncestry } from '@mimir/web/api/globalClientFolder';
import {
  deleteTransferRequest, updateItem } from '@mimir/web/util/api';
import { downloadWithoutNavigationWarning } from '@mimir/web/store/storeMisc';
import { logErrorAndShowToast } from '@mimir/web/util/uiError';
import { sendItemToStorage } from '@mimir/web/util/storageManager';
import type { OngoingDownload } from '@mimir/client-common/model/ongoingDownload';
import type { FolderPathSegmentDto } from '@mimir/common/dto/folderModels';
import type { SearchResultHit, LoadedItem, ClientItem } from '@mimir/common/dto/searchResultHit';
import type { MimirSequence } from '@mimir/common/dto/itemModels';
import { ArchiveRestoreStatus } from '@mimir/common/dto/itemPropTypes';
import { ItemType } from '@mimir/common/dto/itemTypes';
import type { ItemMetaData } from '@mimir/common/dto/fieldValue';
import type { MetadataSnapshot, MetadataErrors } from '@mimir/common/dto/mdfModels';
import type { RestoreMediaResponseDto } from '@mimir/common/dto/restoreMediaDto';
import type { SearchHitSegment } from '@mimir/common/dto/searchModels';
import type { TransferRequestDto } from '@mimir/common/dto/transferRequestDtos';
import { SystemDefaultField } from '@mimir/common/mdf/mdfDefaults';
import { createEmptyMetadata, getItemSystemDefaultField } from '@mimir/common/mdf/MetaDataForm';
import { handleMdfUpdatesToItem } from '@mimir/common/mdf/update';
import TimedTranscript from '@mimir/common/transcript/timedTranscript';
import type { LiveRecordingItemStateDto } from '@mimir/common/dto/liveDtos';
import { PREMIERE_STANDALONE_STORAGE_ID } from '@mimir/common/util/constants';
import { isMediaOnline } from '@mimir/common/item/media';
import { getMediaDuration } from '@mimir/common/util/item';
import { millisecondsToHumanReadableTime } from '@mimir/common/util/date';
import { assert, invariant } from '@mimir/common/util/invariant';
import type { VueSetValue, VueDeleteValue } from '@mimir/common/util/general';
import { getMsFromTimecodeString, isDropFrameTc } from '@mimir/common/util/timecode';
import axios from 'axios';
import isEmpty from 'lodash/isEmpty';
import Vue from 'vue';
import type { Commit, Dispatch, Module, Store } from 'vuex';
import type { State } from './state';
import { timedMdfModule } from './timed-mdf-module';
import type { TimelineModuleState, UpdateCurrentItemPayload } from './timeline-module-state';
import TranscriptWord from '@mimir/common/transcript/transcriptWord';
import { convertVttToSrt } from '@mimir/common/transcript/subtitle';
import { getWordSeparator } from '@mimir/common/transcript/transcriptUtils';
import TranscriptMarker from '@mimir/common/transcript/transcriptMarker';
import { updateCurrentItemMutation } from './timeline-module-mutations';
import type { ClipListEntryDto } from '@mimir/common/dto/clipListDto';
import type { FuzzyValue, FuzzyBounds } from '@mimir/common/util/fuzzyNumbers';
import { integerToFuzzy } from '@mimir/common/util/fuzzyMath';
import { isLoadedItem } from '@mimir/common/search/interfaces';
import { cancelOngoingDownload } from '../globals/cancelOngoingDownload';
import { startPollingTransferProgress } from '../globals/transferProgress';
import { showSimpleDialogFromRequest } from '../dialogs/simple';
import type { SimpleDialogRequest } from '../dialogs/simpleDialogRequest';
import { isInElectron, type ApplicationContext, isInPremiere } from '@mimir/common/context/applicationContext';
import type { UploadItem } from '@mimir/client-common/upload/UploadItem';
import { DefaultNotificationOps } from '../premiere/defaultNotificationOps';
import { ConsoleLogger } from '@mimir/common/util/logging';
import { DefaultOngoingDownloadOps } from '../premiere/defaultOngoingDownloadOps';
import type { LocalStateOps } from '@mimir/client-common/fileSync/localStateOps';
import { getGlobalStore } from '.';
import type { Logger } from '@mimir/common/types/logger';
import type { OngoingDownloadOps } from '@mimir/client-common/fileSync/ongoingDownloadOps';
import type { NotificationOps } from '@mimir/client-common/fileSync/notificationOps';
import type { ItemMediaPlayerOps } from '@mimir/web/model/itemMediaPlayerOps';
import type { TenantConfigDto } from '@mimir/common/dto/tenantModel';
import { exceptionToString } from '@mimir/common/util/exception';
import type { TimedMetadataItem } from '@mimir/common/dto/timedMetadataModels';
import type { Segment } from '@mimir/common/util/intervals';
import type { SetItemPageItemPayload } from './storePayloads';

const timeline = new Timeline();

interface CurrentItemMutationPayload<Prop extends keyof LoadedItem> {
  propName: Prop;
  value: LoadedItem[Prop];
}

// See explanation in store/index.ts for use of 'any'.
const timelineModuleState: any = {
  timeline: timeline,
  currentItem: null,
  currentLiveRecordingState: null,
  selectedTranscriptRange: null,
  downloadInitiated: false,
  transcriptEditMode: false,
  initiatingSubClipDownload: false,
  loadingItem: false,
  isCurrentlyTranslation: null,
  transcriptViewType: 'paragraph',
  syncCursor: true,
  isAudioChannelProxiesDownloadTriggered: false,
  userMadeMetadataChanges: false,
  metadataErrors: {},
  timedTranscriptStateChange: false,
  selectedClip: null,
  sequence: null,
};

const SINGLE_FRAME = 0.05;
const SEARCH_RESULT_ACCURACY_WARNING = 'Add to sequence from ItemDetailsPage for better accuracy';
const LOG_EVENT_ACCURACY_WARNING = 'Log events use approximate ms values';

export const timelineModule: Module<TimelineModuleState, State> = {

  state: timelineModuleState as TimelineModuleState,
  modules: {
    timedMdf: timedMdfModule
  },
  mutations: {
    setSyncCursor: (state, payload: boolean) => state.syncCursor = payload,
    loadingItem: (state, payload: boolean) => state.loadingItem = payload,
    setItemPageItem: (state, payload: {item: LoadedItem | null, isReload?: boolean}) => {
      const {item, isReload} = payload;
      // Backwards compatible change just in case an item doesn't have metadata object.
      if (item !== null && !item.metadata) {
        item.metadata = createEmptyMetadata();
      }

      // If the item is coming from reload event we should update the metadata using handleMdfUpdatesToItem.
      // It prevent's resetting edited metadata values.
      if (isReload && item && state.currentItem) {
        const keysToIterateOver = Object.keys(item) as (keyof LoadedItem)[];
        const vueSet = (v: VueSetValue) => Vue.set(v.object, v.key, v.value);
        const vueDeleter = (v: VueDeleteValue) => Vue.delete(v.object, v.key);
        for (const prop of keysToIterateOver) {
          if (prop === 'metadata' && item?.metadata !== undefined) {
            handleMdfUpdatesToItem(state.currentItem, item.metadata, item.metadataVersion, vueSet, vueDeleter);
          } else {
            Vue.set(state.currentItem, prop, item[prop]);
          }
        }

        // Delete keys from currentItem which are not present in item
        const currentItemKeys = Object.keys(state.currentItem) as (keyof LoadedItem)[];
        for (const prop of currentItemKeys) {
          if (!(prop in item)) {
            Vue.delete(state.currentItem, prop);
          }
        }
      } else {
        state.currentItem = item;
      }
    },
    setCurrentLiveRecordingState: (state, payload: LiveRecordingItemStateDto) => state.currentLiveRecordingState = payload,
    removeCurrentLiveRecordingState: (state) => state.currentLiveRecordingState = null,
    clearTimelineState: (state) => state.timeline.resetMarks(),
    selectedTranscriptRange: (state, payload: TimeRange | null) => state.selectedTranscriptRange = payload,
    transcriptEditMode: (state, payload: boolean) => state.transcriptEditMode = payload,
    initiatedSCDownload: (state, payload: boolean) => state.initiatingSubClipDownload = payload,
    isCurrentlyTranslation: (state, payload: boolean | null) => state.isCurrentlyTranslation = payload,
    transcriptViewType: (state, payload: TranscriptChoice) => state.transcriptViewType = payload,
    isAudioChannelProxiesDownloadTriggered: (state, isDownloadTriggered: boolean) => state.isAudioChannelProxiesDownloadTriggered = isDownloadTriggered,
    syncMetadata: (state, metadata: ItemMetaData) => {
      if (state.currentItem !== null)
        Vue.set(state.currentItem, 'metadata', metadata);
    },
    setMediaRestoreStatusOfCurrentItem: (state, restoreStatus: ArchiveRestoreStatus) => {
      // If there is no archiveInfo in the object, then this should be treated as invalid request
      if (state.currentItem && state.currentItem.archiveInfo) {
        Vue.set(state.currentItem.archiveInfo, 'restoreStatus', restoreStatus);
      }
    },
    setRestoreMediaCompletedInCurrentItem: (state, payload: { itemId: string, highRes: string, mediaOnlineUntil: string }) => {
      if (state.currentItem && state.currentItem.id === payload.itemId) {
        state.currentItem.highRes = payload.highRes;
        if (state.currentItem.archiveInfo) {
          Vue.set(state.currentItem.archiveInfo, 'restoreStatus', ArchiveRestoreStatus.Completed);
          Vue.set(state.currentItem.archiveInfo, 'mediaOnlineUntil', payload.mediaOnlineUntil);
        }
      }
    },
    setUserMadeMetadataChanges: (state, payload: boolean) => state.userMadeMetadataChanges = payload,
    setMetadataErrors: (state, payload: MetadataErrors) => state.metadataErrors = payload,
    setTimedTranscriptStateChange: (state, val: boolean) => state.timedTranscriptStateChange = val,
    updateCurrentItem: updateCurrentItemMutation,
    setCurrentItemProperty: <Prop extends keyof LoadedItem>(state: TimelineModuleState, payload: CurrentItemMutationPayload<Prop>) => {
      if (state.currentItem) Vue.set(state.currentItem, payload.propName, payload.value);
    },
    setTimelineItemMetadataSnapshot: (state, data: MetadataSnapshot) => {
      if (state.currentItem)
        Vue.set(state.currentItem, 'metadataSnapshot', data);
    },
    addToSequence: (state, payload: { clips: ClipListEntryDto[], prepend: boolean }) => {
      if (!payload.clips.length) return;
      if (!state.sequence) state.sequence = { id: '', clips: payload.clips, version: 0 };
      else if (!payload.prepend) state.sequence.clips.push(...payload.clips);
      else state.sequence.clips.unshift(...payload.clips);
    },
    removeFromSequence: (state, index: number) => {
      state.sequence?.clips.splice(index, 1);
      if (!state.sequence?.clips.length) state.sequence = null;
    },
    selectClip: (state, clip: ClipListEntryDto | null) => state.selectedClip = clip,
    editSequence: (state, sequence: MimirSequence | null) => {
      if (!state.sequence || !sequence) {
        state.sequence = sequence;
      } else {
        const req: SimpleDialogRequest = {
          dialogProps: {
            maxWidth: 450,
            icon: 'warning',
            header: 'You\'re already editing a sequence.',
            actionText: 'Replace current sequence',
            secondaryActionText: 'Append segments',
            closeOnActionClick: true,
          },
          callback: (response) => {
            if (response === 'ok') state.sequence = sequence;
            else if (response === 'secondaryOk') state.sequence!.clips.push(...sequence.clips);
          }
        };
        showSimpleDialogFromRequest(req);
      }
    },
  },
  getters: {
    currentLiveRecordingState: state => state.currentLiveRecordingState,
    metadataErrors: state => state.metadataErrors,
    userMadeMetadataChanges: state => state.userMadeMetadataChanges,
    syncCursor: state => state.syncCursor,
    isCurrentlyTranslation: state => state.isCurrentlyTranslation,
    transcriptViewType: state => state.transcriptViewType,
    isLoadingItem: state => state.loadingItem,
    isSubClipDownloadInitiated: state => state.initiatingSubClipDownload,
    transcriptEditMode: state => state.transcriptEditMode,
    selectedTranscriptRange: state => state.selectedTranscriptRange,
    timeline: state => state.timeline,
    item: state => state.currentItem,
    itemSupportsLogging: state => {
      const type = state.currentItem?.itemType;
      return type === ItemType.VIDEO || type === ItemType.AUDIO || type === ItemType.IMAGE;
    },
    isAudioChannelProxiesDownloadTriggered: state => state.isAudioChannelProxiesDownloadTriggered,
    timedTranscriptStateChange: (state) => state.timedTranscriptStateChange,
    sequence: state => state.sequence,
    selectedClip: state => state.selectedClip,
    getClipPosition: (state) => (itemId: string) => {
      return state.sequence ? state.sequence.clips.findIndex(clip => clip.itemId === itemId) : -1;
    },
  },
  actions: {
    saveMetadataForCurrentItem: async ({ state, commit }) => {
      invariant(state.currentItem, 'Expected item to save');
      try {
        const result = await updateItem(state.currentItem.id, {
          metadata: state.currentItem.metadata,
        });
        commit('setUserMadeMetadataChanges', false);
        showToast('Metadata updated.', TOAST.SUCCESS);
        return result;
      } catch (err) {
        logErrorAndShowToast('Failed to save metadata, please try again later.', err, 'ex in saveMetadataForCurrentItem');
      }
    },
    downloadAudioChannelProxies: (context) => {
      context.commit('isAudioChannelProxiesDownloadTriggered', true);
      EventBus.emit(EVENTS.DOWNLOAD_AUDIO_CHANNELS);
    },
    downloadSubtitles: async (
      context,
      payload: {
        applicationContext: ApplicationContext,
        config: SubtitleDownloadConfig,
        virtualClipDurationInMs: number,
        virtualClipStart: FuzzyValue | null
      }
    ) => {
      const { applicationContext, config, virtualClipDurationInMs, virtualClipStart } = payload;
      const item = context.state.currentItem;
      if (item === null) return;
      const logger = new ConsoleLogger();
      let srtString: string | undefined;
      const timeRange = context.getters.timeline.getTimeRange(virtualClipDurationInMs, virtualClipStart);
      try {
        const isTranscript = item.languageCode === config.language;
        srtString = await getSrtString(item, timeRange, isTranscript, applicationContext);
      } catch (error) {
        logger.error(`Error while downloading transcript: ${exceptionToString(error)}`);
        showToast('Error while getting transcript', TOAST.ERROR);
        return;
      }

      if (config.type === SrtType.Avid) {
        srtString = getAvidDsSrt(logger, srtString, config.mediaFrameRate, config.timecode);
      }

      const ext = config.extension || (config.type === SrtType.Standard ? 'srt' : 'txt');
      if (!srtString) {
        let errorMsg = 'Unable to download SRT subtitles.';
        if (timeRange) errorMsg = 'No subtitle available for selected time range';
        showToast(errorMsg, TOAST.INFO);
      } else if (isInPremiere(applicationContext)) {
        const downloadPath = context.rootGetters.downloadPath;
        if (isEmpty(downloadPath)) {
          // TODO - improve usability - we are able to bring up a dialog to allow the user to fix it instead of showing an error.
          showToast('Please configure media download path from the Settings page.', TOAST.ERROR);
          return;
        }
        // Windows doesn't allow to create file with ':' in the title
        const timeRangePrefix = timeRange ? `${millisecondsToHumanReadableTime(timeRange.start)} - ${millisecondsToHumanReadableTime(timeRange.end)}_`.replace(/:/g, ',') : '';
        const services = {
          applicationContext,
          logger: new ConsoleLogger(),
        };
        const userDataSystemPath = getUserDataPath(applicationContext);
        await writeAndImportSrt(services, userDataSystemPath, item, srtString, ext, timeRangePrefix, virtualClipStart, virtualClipDurationInMs);
      } else {
        const fileName = generateSrtFileName(getItemSystemDefaultField(item, SystemDefaultField.Title), ext);
        attachDownloadLink(srtString, fileName);
      }
    },
    downloadToStorage: async ({ rootGetters }, payload: {
      applicationContext: ApplicationContext,
      item: ClientItem | null,
      storageId: string,
      downloadPath: string,
      localStateOps: LocalStateOps | undefined,
    }) => {
      const { applicationContext, item, storageId, downloadPath, localStateOps } = payload;

      invariant(item, 'Expected current item');
      try {
        const result: OngoingDownload = await sendItemToStorage(
          storageId,
          item.id,
          getItemSystemDefaultField(item, SystemDefaultField.Title),
          item.aaf ? 'aaf' : 'highres'
        );
        result.initiatedFromStorageDownloadMenu = true;

        if (item.archiveInfo && !isMediaOnline(item) && result.highresIsOffline) {
          // As this item is offline (in cold storage) and a transfer request was successfully created, it's safe to assume
          // that restore is in progress. Without setting the following prop, the UI will not reflect restore status
          Vue.set(item.archiveInfo, 'restoreStatus', ArchiveRestoreStatus.InProgress);
        }
        const ongoingDownloadOps = new DefaultOngoingDownloadOps();
        ongoingDownloadOps.addOrUpdateOngoingDownload(result);
        if (result.storageId) {
          const notificationOps = new DefaultNotificationOps(applicationContext);
          const services = {
            applicationContext,
            notificationOps,
            logger: new ConsoleLogger(),
            ongoingDownloadOps,
            localStateOps,
          };
          let userDataSystemPath: string | undefined;
          if (isInPremiere(applicationContext) || isInElectron(applicationContext)) {
            userDataSystemPath = getUserDataPath(applicationContext);
          }
          const config = {
            downloadPath,
            userDataSystemPath,
            shouldUsePremierePreferences: rootGetters.usePremierePreferenceForScaleToFrame,
            skippedFileExtensions: (rootGetters.tenantConfig as TenantConfigDto).skippedFileExtensionsForPremiere,
          };
          startPollingTransferProgress(services, config, result.storageId);
        }

      } catch (error) {
        logErrorAndShowToast('Download could not be started, please try again later.', error, 'ex in downloadToStorage');
      }
    },
    cancelOngoingTransfer: async ({ rootGetters }, payload: {
      applicationContext: ApplicationContext,
      currentTransfer: TransferRequestDto,
      ongoingUploads: UploadItem[],
      localStateOps: LocalStateOps | undefined,
      logger: Logger,
      ongoingDownloadOps: OngoingDownloadOps,
      notificationOps: NotificationOps,
    }) => {
      const { applicationContext, currentTransfer, localStateOps, logger, ongoingDownloadOps, notificationOps } = payload;
      try {
        const services = {
          applicationContext,
          notificationOps,
          logger,
          ongoingDownloadOps,
          localStateOps,
        };
        await cancelOngoingDownload(services, currentTransfer, (rootGetters.tenantConfig as TenantConfigDto).skippedFileExtensionsForPremiere);
        // Prevent passing delete request from server when in standalone mode
        if (currentTransfer.storageId !== PREMIERE_STANDALONE_STORAGE_ID) {
          await deleteTransferRequest(currentTransfer, true);
        }
      } catch (error) {
        logErrorAndShowToast('Could not cancel transfer, please try again later.', error, 'ex in cancelOngoingTransfer');
      }
    },
    setItemPageItem: async (context, payload: SetItemPageItemPayload) => {
      const { applicationContext, item, isReload, isProxyMode, playerOps } = payload;

      context.commit('setItemPageItem', {item, isReload});
      context.commit('isAudioChannelProxiesDownloadTriggered', false);

      if (item === null) {
        return;
      }

      const selectedClip = context.state.selectedClip;
      if (selectedClip && selectedClip.itemId === item.id) {
        timeline.sourceInPosition = cloneDeep(selectedClip.in);
        timeline.sourceOutPosition = cloneDeep(selectedClip.out);

        const timeRange = timeline.getTimeRange(
          playerOps.getVirtualClipDurationObs().currentValue().approximate * 1000.0,
          playerOps.getVirtualClipStartObs().currentValue(),
        );

        context.commit('selectedTranscriptRange', timeRange);
      } else {
        context.commit('clearTimelineState');
      }

      if (isInElectron(applicationContext)) {
        // set local path info
        if (!item.folderParents || item.folderParents.length === 0) {
          const localPathInfo = prepareLocalPathInfo(isProxyMode, applicationContext, item, null);
          void context.dispatch('setLocalPathInfo', { id: item.id, localPathInfo });
        } else {
          const paths = await getFolderAncestry([item.id], 'item');
          const resolvedPaths = paths[item.id];
          if (!resolvedPaths) {
            const localPathInfo = prepareLocalPathInfo(isProxyMode, applicationContext, item, null);
            void context.dispatch('setLocalPathInfo', { id: item.id, localPathInfo });
          } else {
            const flattenItemAncestors = Object.entries(resolvedPaths.paths).reduce(
              (acc: FolderPathSegmentDto[][], [_, ancestor]) => acc.concat(ancestor),
              [],
            );
            invariant(flattenItemAncestors.filter(el => el == null).length == 0, 'setItemPageItem: Did not expect any null elements');
            const localPathInfo = prepareLocalPathInfo(isProxyMode, applicationContext, item, flattenItemAncestors);
            void context.dispatch('setLocalPathInfo', { id: item.id, localPathInfo });
          }
        }
      }
    },
    timelineModuleCleanup: (context, playerOps: ItemMediaPlayerOps) => {
      playerOps.clear();
      if (context.state.currentItem !== null) {
        if (context.state.currentItem.timedTranscript) {
          context.state.currentItem.timedTranscript.cleanup();
          context.state.currentItem.timedTranscript = null;
        }
        if (context.state.currentItem.timedTranslatedTranscript) {
          context.state.currentItem.timedTranslatedTranscript.cleanup();
          context.state.currentItem.timedTranslatedTranscript = null;
        }
      }
      context.state.timeline.resetMarks();
      context.commit('isCurrentlyTranslation', null);

      // Reset timed mdf module
      return context.dispatch('timedMdf/timedMdfModuleCleanup');
    },
    /**
     * This action will be fired when an item's archive to Glacier tag is set but actually is not taken to Glacier yet.
     * In this period (setting Glacier tag => actually take to Glacier), if a user tries to restore a media
     * then restoreMedia API replies with a downloadable highRes URL and store will fire this action
     * to set the highRes download URL and enable other download buttons with a arbitrary mediaOnlineUntil date
     */
    restoreMediaCompletedForOnlineMedia: (context, payload: RestoreMediaResponseDto) => {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      const restoreCompletedPayload = {
        itemId: payload.itemId,
        highRes: payload.highRes,
        mediaOnlineUntil: tomorrow.toISOString(),
      };
      if (context.state.currentItem && context.state.currentItem.id === payload.itemId) {
        context.commit('setRestoreMediaCompletedInCurrentItem', restoreCompletedPayload);
      }
      context.commit('setRestoreMediaCompletedInSearchResult', restoreCompletedPayload);
    },
    toggleTranscriptStateChange: (context) => {
      context.commit('setTimedTranscriptStateChange', !context.state.timedTranscriptStateChange);
    },
    addItemsToSequence: (context, payload: {
      items: (LoadedItem | SearchResultHit)[],
      prepend?: boolean,
      playerOps: ItemMediaPlayerOps,
    }) => {
      const { items, prepend, playerOps } = payload;
      const applyDownmix = (context.rootGetters.featureFlags as TenantConfigDto['featureFlags'])?.segmentAudioToggles ?? false;
      const clips: ClipListEntryDto[] = [];
      items.forEach(item => {
        const itemId = item.virtualClipInfo ? item.virtualClipInfo.masterClipId : item.id;
        const inSequence = context.state.sequence?.clips.filter(c => c.itemId === itemId).length || 0;
        const inClips = clips.filter(c => c.itemId === itemId).length;
        const clipNumber = inSequence + inClips + 1;
        const clip = createClipFromItem(playerOps, item, clipNumber, applyDownmix);
        if (clip) clips.push(clip);
      });
      context.commit('addToSequence', { clips, prepend });
    },
    addEventsToSequence: (context, payload: {
      events: (TimedMetadataItem | SearchHitSegment)[],
    }) => {
      const clips: ClipListEntryDto[] = [];
      const currentItem = context.state.currentItem;
      invariant(currentItem, 'Expected current item');
      const itemId = currentItem.id;
      const inSequence = context.state.sequence?.clips.filter(c => c.itemId === itemId).length || 0;
      payload.events.forEach(event => {
        const inClips = clips.filter(c => c.itemId === itemId).length;
        const clipNumber = inSequence + inClips + 1;
        const clip = createClipFromEvent(event, clipNumber, currentItem);
        if (clip) clips.push(clip);
      });
      context.commit('addToSequence', { clips });
    }
  },
};

function createClipFromItem(
  playerOps: ItemMediaPlayerOps,
  item: SearchResultHit | LoadedItem,
  clipNumber: number,
  applyDownmix: boolean,
): ClipListEntryDto | undefined {
  let clipRange: FuzzyBounds;
  let audioMapping: number[][] | undefined = undefined;
  let durationInSeconds: FuzzyValue | undefined;

  if (isLoadedItem(item)) {
    durationInSeconds = { approximate: getMediaDuration(item) / 1000, accuracyWarning: SEARCH_RESULT_ACCURACY_WARNING }

    // We're in ItemDetailsPage and we can get precise bounds
    clipRange = playerOps.getVirtualClipSourceMediaRangeObs().currentValue().bounds;
    const audioTracks = playerOps.getAudioTrackConfigsObs().currentValue();
    if (audioTracks.length && applyDownmix) {
      audioMapping = Array.from({ length: audioTracks.length });
      const solo = audioTracks.some(track => track.isSolo);
      for (let i = 0; i < audioMapping.length; i++) {
        audioMapping[i] = [];
        if (!audioTracks[i].isMuted && (!solo || audioTracks[i].isSolo)) {
          audioMapping[i][i] = 1;
          audioMapping[i].fill(0, 0, i);
        }
      }
    }
  } else if (!item.virtualClipInfo) {
    durationInSeconds = { approximate: getMediaDuration(item) / 1000, accuracyWarning: SEARCH_RESULT_ACCURACY_WARNING }
    clipRange = {
      start: integerToFuzzy(0),
      end: durationInSeconds,
    };
  } else {
    clipRange = {
      start: { approximate: item.virtualClipInfo.inPoint / 1000, accuracyWarning: SEARCH_RESULT_ACCURACY_WARNING },
      end: { approximate: item.virtualClipInfo.outPoint / 1000, accuracyWarning: SEARCH_RESULT_ACCURACY_WARNING }
    };
  }
  const clip = {
    itemId: item.virtualClipInfo?.masterClipId ?? item.id,
    in: timeline.sourceInPosition ?? clipRange.start,
    out: timeline.sourceOutPosition ?? clipRange.end,
    title: item.metadata.formData[SystemDefaultField.Title] + ' #' + clipNumber,
    audioMapping,
  };
  if (clip.out.approximate - clip.in.approximate > SINGLE_FRAME) return clip;
  else showToast('Segment is too short to be added to sequence', TOAST.ERROR);
}

function createClipFromEvent(event: TimedMetadataItem | Segment, clipNumber: number, relatedItem: LoadedItem): ClipListEntryDto | undefined {
  const startMs = 'id' in event ? event.startMs : event.start;
  const endMs = 'id' in event ? event.endMs : event.end;
  const clip = {
    itemId: relatedItem.id,
    in: { approximate: startMs / 1000, accuracyWarning: LOG_EVENT_ACCURACY_WARNING },
    out: { approximate: endMs / 1000, accuracyWarning: LOG_EVENT_ACCURACY_WARNING },
    title: relatedItem.metadata.formData[SystemDefaultField.Title] + ' #' + clipNumber.toString(),
    audioMapping: undefined,
  };
  if (clip.out.approximate - clip.in.approximate > SINGLE_FRAME) return clip;
  else showToast('Event is too short to be added to sequence', TOAST.ERROR);
}

const generateSrtFileName = (itemTitle: string, ext: string): string => {
  if (!isEmpty(itemTitle)) {
    const index = itemTitle.lastIndexOf('.');
    return index > -1
      ? `${itemTitle.substring(0, index)}.${ext}`
      : `${itemTitle}.${ext}`;
  }
  return `Subtitles.${ext}`;
};

const attachDownloadLink = (text: string, fileName: string) => {
  const encodedChars = new Uint8Array([
    0xEF, 0xBB, 0xBF, // UTF-8 BOM
    ...new TextEncoder().encode(text), // convert to UTF-8
  ]);

  downloadWithoutNavigationWarning(encodedChars.buffer, fileName);
};

const getFrameCount = (timecode?: string) => {
  if (!timecode) return 0;
  if (isDropFrameTc(timecode)) {
    return Number(timecode.substr(timecode.indexOf(';') + 1));
  }

  const [, , , fc] = timecode.split(':');
  return fc ? +fc : 0;
};

const pad2 = (x: number) => x.toString().padStart(2, '0');

const getAvidDsSrt = (logger: Logger, srt?: string, fps?: number, timecode?: string) => {

  if (!srt || !fps) return;

  const maxFcPlusOne = Math.ceil(fps);

  const offsetInSecond = (getMsFromTimecodeString(logger, timecode) || 0) / 1000;
  const offsetFc = Math.floor(getFrameCount(timecode)) % maxFcPlusOne;

  // On some edge cases, the calculated frame count would be one off.
  const getAdjustedTimestamp = (h: number, m: number, s: number, ms: number) => {
    const totalSecond = (h * 3600 + m * 60 + s);
    const fc = Math.floor(+ms * (fps / 1000)) % maxFcPlusOne;

    const overflow = (fc + offsetFc) >= maxFcPlusOne;
    const fcWithOffset = overflow ? (fc + offsetFc) - maxFcPlusOne : (fc + offsetFc);
    const totalSecondWithOffset = totalSecond + offsetInSecond + +overflow;

    const ah = Math.floor(totalSecondWithOffset / 3600);
    const am = Math.floor((totalSecondWithOffset - (ah * 3600)) / 60);
    const as = totalSecondWithOffset % 60;

    return `${pad2(ah)}:${pad2(am)}:${pad2(as)}:${pad2(fcWithOffset)}`;
  };

  const lines = srt.split('\n');

  const avidSrt = ['<end subtitles>\n'];
  const lineCount = lines.length;
  let skipNext = false;

  for (let i = lineCount - 1; i >= 0; --i) {
    const line = lines.pop()!;

    if (skipNext) {
      skipNext = false;
      continue;
    }

    const isTimeSegment = /^\d{2}:\d{2}:\d{2},\d{3} --> \d{2}:\d{2}:\d{2},\d{3}$/.test(line);
    if (isTimeSegment) {
      skipNext = true; // next line contains subtitle segment number

      const [sh, sm, ss, sms, eh, em, es, ems] = line.match(/\d+/g) || [];
      assert(sh && sm && ss && sms && eh && es && ems, 'Expected valid time segment syntax');
      avidSrt.push(`${getAdjustedTimestamp(+sh, +sm, +ss, +sms)} ${getAdjustedTimestamp(+eh, +em, +es, +ems)}`);
    } else {
      avidSrt.push(line);
    }
  }

  avidSrt.push('<begin subtitles>\n');

  return avidSrt.reverse().join('\n');
};

async function getSrtString(
  item: LoadedItem,
  timeRange: TimeRange | null,
  isTranscript: boolean,
  applicationContext: ApplicationContext,
) {
  if (!isInPremiere(applicationContext)) {
    const srtUrl = isTranscript ? item.srtUrl : item.translationSrtUrl;
    const srtString = await axios.get(srtUrl).then((result: any) => result.data) as string;
    return srtString;
  }
  const timedTranscript: TimedTranscript | undefined = isTranscript ? item.timedTranscript : item.timedTranslatedTranscript;
  invariant(timedTranscript, 'Expect timedTranscript to exist on loaded item');

  const transcriptWords = timedTranscript.flatten();
  const subtitleMarkers = timedTranscript.subtitleMarkers;
  let transcriptWordsInRange: TranscriptWord[] = [];
  let subtitleMarkersInRange: TranscriptMarker[] = [];

  if (timeRange) {
    let firstWordInRangeIndex = -1;
    let lastWordInRangeIndex = -1;

    // let's filter the transcript words in range
    let len = transcriptWords.length;
    for (let i = 0; i < len; ++i) {
      const t = transcriptWords[i];
      // As the words are sorted and words don't overlap, if a word's end time exceeds the right boundary we can stop iterating
      if (t.endTime > timeRange.end) break;
      // We only include the words that are fully 'inside' the boundary (e.g., we exclude the words that are 'on' the boundary)
      if (t.startTime >= timeRange.start && t.endTime <= timeRange.end) {
        const startTime = t.startTime - timeRange.start;
        const endTime = t.endTime - timeRange.start;
        transcriptWordsInRange.push(new TranscriptWord(t.content, startTime, endTime, t.speaker, t.edited));
        lastWordInRangeIndex = i;
        if (firstWordInRangeIndex === -1) firstWordInRangeIndex = i;
      }
    }

    // let's filter the subtitle markers in range
    len = subtitleMarkers.length;
    for (let i = 0; i < len; ++i) {
      const sm = subtitleMarkers[i];
      if (sm.p > lastWordInRangeIndex + 1) break;
      if (sm.p > firstWordInRangeIndex) {
        subtitleMarkersInRange.push(new TranscriptMarker(sm.p - firstWordInRangeIndex));
      }
    }

    // Add a marker at the end
    const lastMarkerIndex = subtitleMarkersInRange.length - 1;
    if (lastMarkerIndex >= 0 && subtitleMarkersInRange[lastMarkerIndex].p !== lastWordInRangeIndex + 1) {
      subtitleMarkersInRange.push(new TranscriptMarker(lastWordInRangeIndex + 1));
    }
  } else {
    transcriptWordsInRange = transcriptWords;
    subtitleMarkersInRange = subtitleMarkers;
  }

  const vttFileContents = TimedTranscript.convertToVtt(transcriptWordsInRange, subtitleMarkersInRange, getWordSeparator(item.languageCode));
  const srtFileContents = convertVttToSrt(vttFileContents);
  return srtFileContents;
}

export const setCurrentItemPropertyInTimeLineModule = <PropertyToMutate extends keyof LoadedItem>(
  timeLineModule: Store<State>,
  prop: PropertyToMutate,
  value: LoadedItem[PropertyToMutate]
) => {
  const payload: CurrentItemMutationPayload<PropertyToMutate> = { propName: prop, value };
  timeLineModule.commit('setCurrentItemProperty', payload);
};

export const commitUpdateCurrentItem = (commit: Commit, payload: UpdateCurrentItemPayload) => {
  commit('updateCurrentItem', payload);
};

export const dispatchSubtitleDownload = (
  dispatch: Dispatch,
  applicationContext: ApplicationContext,
  config: SubtitleDownloadConfig,
  virtualClipDurationInMs: number,
  virtualClipStart: FuzzyValue | null,
) => {
  void dispatch('downloadSubtitles', { applicationContext, config, virtualClipDurationInMs, virtualClipStart });
};

export async function storeDispatchCancelOngoingTransfer(
  applicationContext: ApplicationContext,
  currentTransfer: TransferRequestDto,
  ongoingUploads: UploadItem[],
  localStateOps: LocalStateOps | undefined,
  logger: Logger,
  ongoingDownloadOps: OngoingDownloadOps,
  notificationOps: NotificationOps,
): Promise<void> {
  await getGlobalStore().dispatch('cancelOngoingTransfer', {
    applicationContext,
    currentTransfer,
    ongoingUploads,
    localStateOps,
    logger,
    ongoingDownloadOps,
    notificationOps,
  });
}
